// src/components/Property/Owners/OwnersDialog.jsx

import React, { useEffect, useMemo, useState } from "react"
import {
    Button,
    Card,
    CardContent,
    CardHeader,
    Checkbox,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Divider,
    FormControl,
    Grid,
    IconButton,
    InputLabel,
    Menu,
    MenuItem,
    Select,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Typography,
    Tooltip,
    CircularProgress,
} from "@mui/material"
import AddIcon from "@mui/icons-material/Add"
import DeleteIcon from "@mui/icons-material/Delete"
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown"
import EditIcon from "@mui/icons-material/Edit"
import moment from "moment"
import { DatePicker } from "@mui/x-date-pickers"
import { useSnackbar } from "notistack"

import {api} from "../../../../../services/Main/Api";
// import { getCondoId } from "../../../services/Main/AuthStorage"

/* ================= Helpers (máscaras / validações) ================= */
const onlyDigits = (v) => String(v ?? "").replace(/\D/g, "")

const isEmailValid = (email) => {
    if (!email) return true
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(email).trim())
}

const formatCpfCnpj = (value) => {
    const v = onlyDigits(value).slice(0, 14)
    if (v.length <= 11) {
        return v
            .replace(/^(\d{3})(\d)/, "$1.$2")
            .replace(/^(\d{3})\.(\d{3})(\d)/, "$1.$2.$3")
            .replace(/^(\d{3})\.(\d{3})\.(\d{3})(\d{1,2})$/, "$1.$2.$3-$4")
    }
    return v
        .replace(/^(\d{2})(\d)/, "$1.$2")
        .replace(/^(\d{2})\.(\d{3})(\d)/, "$1.$2.$3")
        .replace(/^(\d{2})\.(\d{3})\.(\d{3})(\d)/, "$1.$2.$3/$4")
        .replace(/^(\d{2})\.(\d{3})\.(\d{3})\/(\d{4})(\d{1,2})$/, "$1.$2.$3/$4-$5")
}

const formatBRPhone = (value) => {
    const v = onlyDigits(value).slice(0, 11)
    if (v.length <= 10) {
        return v
            .replace(/^(\d{2})(\d)/, "($1) $2")
            .replace(/^(\(\d{2}\)\s\d{4})(\d)/, "$1-$2")
    }
    return v
        .replace(/^(\d{2})(\d)/, "($1) $2")
        .replace(/^(\(\d{2}\)\s\d{5})(\d)/, "$1-$2")
}

const MARITAL_STATUS_CHOICES = [
    { value: "single", label: "Solteiro(a)" },
    { value: "married", label: "Casado(a)" },
    { value: "divorced", label: "Divorciado(a)" },
    { value: "separated", label: "Separado(a)" },
    { value: "widowed", label: "Viúvo(a)" },
    { value: "stable_union", label: "União estável" },
]

const emptyOwner = {
    id: null,
    name: "",
    cpf_cnpj: "",
    phone: "",
    email: "",
    marital_status: "",
    nationality: "Brasileira",
    rg: "",
    address: "",
    birth_date: null,
    created_at: null,
}

/* ================= Component ================= */

export default function Owner({
                                  unitId, // ✅ agora é obrigatório para buscar
                                  open,
                                  onClose,
                              }) {
    const { enqueueSnackbar } = useSnackbar()

    const [owners, setOwners] = useState({ results: [], count: 0 })
    const [selectedIds, setSelectedIds] = useState([])
    const [anchorEl, setAnchorEl] = useState(null)
    const openMenu = Boolean(anchorEl)

    const [formOpen, setFormOpen] = useState(false)
    const [confirmOpen, setConfirmOpen] = useState(false)

    const [editMode, setEditMode] = useState(false)
    const [editId, setEditId] = useState(null)
    const [ownerForm, setOwnerForm] = useState(emptyOwner)
    const [errors, setErrors] = useState({})
    const [loading, setLoading] = useState(false)

    const fetchOwners = async () => {
        if (!unitId) return
        setLoading(true)
        try {
            // se precisar condo, pluga aqui:
            // const condo = getCondoId()
            // const { data } = await api.get("unit_owner/", { params: { unit_id: unitId, condo } })

            const { data } = await api.get("unit_owner/", { params: { unit_id: unitId } })

            // ✅ suportar tanto lista direta quanto paginação (results/count)
            const list = Array.isArray(data) ? data : data?.results || []
            const count = Number(data?.count ?? list.length)

            setOwners({ results: list, count })
            setSelectedIds([])
        } catch (e) {
            console.error(e)
            enqueueSnackbar("Erro ao buscar proprietários.", { variant: "error" })
        } finally {
            setLoading(false)
        }
    }

    // ✅ busca ao abrir e quando trocar unitId
    useEffect(() => {
        fetchOwners()
    }, [])

    const allSelected = useMemo(() => {
        const list = owners?.results || []
        return list.length > 0 && selectedIds.length === list.length
    }, [owners, selectedIds])

    const isSelected = (id) => selectedIds.includes(id)

    const handleSelectAll = (event) => {
        const list = owners?.results || []
        setSelectedIds(event.target.checked ? list.map((o) => o.id) : [])
    }

    const handleSelectOne = (id) => {
        setSelectedIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]))
    }

    const handleMenuClick = (event) => setAnchorEl(event.currentTarget)
    const handleMenuClose = () => setAnchorEl(null)

    const openCreate = () => {
        setEditMode(false)
        setEditId(null)
        setOwnerForm({ ...emptyOwner })
        setErrors({})
        setFormOpen(true)
    }

    const openEdit = (owner) => {
        setEditMode(true)
        setEditId(owner.id)
        setOwnerForm({
            ...emptyOwner,
            ...owner,
            cpf_cnpj: formatCpfCnpj(owner.cpf_cnpj),
            phone: formatBRPhone(owner.phone),
        })
        setErrors({})
        setFormOpen(true)
    }

    const handleDialogChange = (e) => {
        const { name, value } = e.target
        if (name === "cpf_cnpj") {
            setOwnerForm((prev) => ({ ...prev, cpf_cnpj: formatCpfCnpj(value) }))
            return
        }
        if (name === "phone") {
            setOwnerForm((prev) => ({ ...prev, phone: formatBRPhone(value) }))
            return
        }
        setOwnerForm((prev) => ({ ...prev, [name]: value }))
    }

    const validateFields = () => {
        const newErrors = {}

        if (!ownerForm.name?.trim()) newErrors.name = "Campo obrigatório"
        // if (!ownerForm.cpf_cnpj?.trim()) newErrors.cpf_cnpj = "Campo obrigatório"
        //
        // const digits = onlyDigits(ownerForm.cpf_cnpj)
        // if (digits && !(digits.length === 11 || digits.length === 14)) {
        //     newErrors.cpf_cnpj = "CPF deve ter 11 dígitos ou CNPJ 14 dígitos"
        // }
        //
        // if (!ownerForm.email?.trim()) newErrors.email = "Campo obrigatório"
        // if (ownerForm.email && !isEmailValid(ownerForm.email)) newErrors.email = "Email inválido"
        //
        // if (ownerForm.phone) {
        //     const p = onlyDigits(ownerForm.phone)
        //     if (p.length > 0 && p.length < 10) newErrors.phone = "Telefone incompleto"
        // }

        setErrors(newErrors)
        return Object.keys(newErrors).length === 0
    }

    const handleSaveOwner = async () => {
        if (!validateFields()) return
        if (!unitId) {
            enqueueSnackbar("Unit ID não informado.", { variant: "error" })
            return
        }

        const payload = {
            ...ownerForm,
            unit: unitId, // ✅ geralmente o backend precisa disso no POST
            cpf_cnpj: onlyDigits(ownerForm.cpf_cnpj),
            phone: onlyDigits(ownerForm.phone),
            birth_date: ownerForm.birth_date ? moment(ownerForm.birth_date).format("YYYY-MM-DD") : null,
        }

        try {
            if (editMode) {
                await api.put(`unit_owner/${editId}/`, payload)
                enqueueSnackbar("Proprietário atualizado com sucesso!", { variant: "success" })
            } else {
                await api.post("unit_owner/", payload)
                enqueueSnackbar("Proprietário criado com sucesso!", { variant: "success" })
            }

            setFormOpen(false)
            setOwnerForm(emptyOwner)
            setEditMode(false)
            setEditId(null)
            setErrors({})

            await fetchOwners()
        } catch (e) {
            console.error(e)
            enqueueSnackbar("Erro ao salvar proprietário.", { variant: "error" })
        }
    }

    const handleDeleteClick = () => {
        setConfirmOpen(true)
        handleMenuClose()
    }

    const handleDeleteConfirmed = async () => {
        try {
            await Promise.all(selectedIds.map((id) => api.delete(`unit_owner/${id}/`)))
            enqueueSnackbar("Proprietário(s) deletado(s) com sucesso!", { variant: "success" })
            setSelectedIds([])
            setConfirmOpen(false)
            await fetchOwners()
        } catch (e) {
            console.error(e)
            enqueueSnackbar("Erro ao deletar proprietário(s).", { variant: "error" })
        }
    }

    return (
        <>
            {/* aqui você pode envolver com Dialog maior se quiser; mantive seu layout */}
            <Card variant="outlined" sx={{ mt: 1 }}>
                <CardHeader
                    title="Proprietários"
                />
                <Divider />

                <CardContent>
                    {!unitId ? (
                        <Typography variant="body2" color="error">
                            unitId não informado.
                        </Typography>
                    ) : loading ? (
                        <Stack direction="row" spacing={1} alignItems="center">
                            <CircularProgress size={18} />
                            <Typography variant="body2">Carregando proprietários...</Typography>
                        </Stack>
                    ) : (
                        <>
                            <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
                                <Button variant="contained" size="small" startIcon={<AddIcon />} onClick={openCreate}>
                                    Adicionar proprietário
                                </Button>

                                <Button
                                    variant="outlined"
                                    size="small"
                                    startIcon={<KeyboardArrowDownIcon />}
                                    onClick={handleMenuClick}
                                    disabled={selectedIds.length === 0}
                                >
                                    Ações {selectedIds.length > 0 ? `(${selectedIds.length})` : ""}
                                </Button>

                                <Menu anchorEl={anchorEl} open={openMenu} onClose={handleMenuClose}>
                                    <MenuItem onClick={handleDeleteClick}>
                                        <DeleteIcon fontSize="small" sx={{ mr: 1 }} />
                                        Excluir selecionados
                                    </MenuItem>
                                </Menu>
                            </Stack>

                            <TableContainer>
                                <Table>
                                    <TableHead>
                                        <TableRow>
                                            <TableCell padding="checkbox">
                                                <Checkbox checked={allSelected} onChange={handleSelectAll} />
                                            </TableCell>
                                            <TableCell>
                                                <strong>Nome</strong>
                                            </TableCell>
                                            <TableCell>
                                                <strong>CPF/CNPJ</strong>
                                            </TableCell>
                                            <TableCell>
                                                <strong>Email</strong>
                                            </TableCell>
                                            <TableCell>
                                                <strong>Telefone</strong>
                                            </TableCell>
                                            <TableCell>
                                                <strong>Estado civil</strong>
                                            </TableCell>
                                            <TableCell>
                                                <strong>Nascimento</strong>
                                            </TableCell>
                                            <TableCell>
                                                <strong>Criado em</strong>
                                            </TableCell>
                                            <TableCell align="right">
                                                <strong>Ações</strong>
                                            </TableCell>
                                        </TableRow>
                                    </TableHead>

                                    <TableBody>
                                        {(owners?.results || []).map((o) => (
                                            <TableRow key={o.id} hover sx={{ cursor: "pointer" }} onClick={() => openEdit(o)}>
                                                <TableCell padding="checkbox" onClick={(e) => e.stopPropagation()}>
                                                    <Checkbox checked={isSelected(o.id)} onChange={() => handleSelectOne(o.id)} />
                                                </TableCell>
                                                <TableCell>{o.name}</TableCell>
                                                <TableCell>{formatCpfCnpj(o.cpf_cnpj)}</TableCell>
                                                <TableCell>{o.email}</TableCell>
                                                <TableCell>{formatBRPhone(o.phone)}</TableCell>
                                                <TableCell>
                                                    {MARITAL_STATUS_CHOICES.find((x) => x.value === o.marital_status)?.label || "-"}
                                                </TableCell>
                                                <TableCell>
                                                    {o.birth_date ? moment(o.birth_date).format("DD/MM/YYYY") : "-"}
                                                </TableCell>
                                                <TableCell>
                                                    {o.created_at ? moment(o.created_at).format("DD/MM/YYYY [às] HH:mm") : "-"}
                                                </TableCell>
                                                <TableCell align="right" onClick={(e) => e.stopPropagation()}>
                                                    <Tooltip title="Editar">
                                                        <IconButton size="small" onClick={() => openEdit(o)}>
                                                            <EditIcon fontSize="small" />
                                                        </IconButton>
                                                    </Tooltip>
                                                </TableCell>
                                            </TableRow>
                                        ))}

                                        {(owners?.results || []).length === 0 && (
                                            <TableRow>
                                                <TableCell colSpan={9} align="center">
                                                    Nenhum proprietário cadastrado.
                                                </TableCell>
                                            </TableRow>
                                        )}
                                    </TableBody>
                                </Table>
                            </TableContainer>

                            <Typography variant="caption" sx={{ display: "block", mt: 2, opacity: 0.8 }}>
                                Dica: clique em uma linha para editar.
                            </Typography>
                        </>
                    )}
                </CardContent>
            </Card>

            {/* Dialog de confirmação de exclusão */}
            <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)}>
                <DialogTitle>Confirmar Exclusão</DialogTitle>
                <DialogContent>
                    <Typography>
                        Tem certeza que deseja deletar {selectedIds.length} {selectedIds.length === 1 ? "item" : "itens"}?
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setConfirmOpen(false)} size="small">
                        Cancelar
                    </Button>
                    <Button onClick={handleDeleteConfirmed} color="error" variant="contained" size="small">
                        Deletar
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Dialog do formulário (criar/editar) */}
            <Dialog open={formOpen} onClose={() => setFormOpen(false)} fullWidth maxWidth="sm">
                <DialogTitle>{editMode ? "Editar Proprietário" : "Novo Proprietário"}</DialogTitle>
                <Divider />
                <DialogContent>
                    <Grid container spacing={2} mt={1}>
                        <Grid item xs={12}>
                            <TextField
                                label="Nome"
                                name="name"
                                value={ownerForm.name}
                                onChange={handleDialogChange}
                                fullWidth
                                size="small"
                                error={!!errors.name}
                                helperText={errors.name}
                            />
                        </Grid>

                        <Grid item xs={12} sm={6}>
                            <TextField
                                label="CPF/CNPJ"
                                name="cpf_cnpj"
                                value={ownerForm.cpf_cnpj}
                                onChange={handleDialogChange}
                                fullWidth
                                size="small"
                                error={!!errors.cpf_cnpj}
                                helperText={errors.cpf_cnpj || "CPF: 11 dígitos | CNPJ: 14 dígitos"}
                            />
                        </Grid>

                        <Grid item xs={12} sm={6}>
                            <TextField
                                label="RG"
                                name="rg"
                                value={ownerForm.rg}
                                onChange={handleDialogChange}
                                fullWidth
                                size="small"
                            />
                        </Grid>

                        <Grid item xs={12} sm={6}>
                            <TextField
                                label="Telefone"
                                name="phone"
                                value={ownerForm.phone}
                                onChange={handleDialogChange}
                                fullWidth
                                size="small"
                                error={!!errors.phone}
                                helperText={errors.phone}
                            />
                        </Grid>

                        <Grid item xs={12} sm={6}>
                            <TextField
                                label="Email"
                                name="email"
                                value={ownerForm.email}
                                onChange={handleDialogChange}
                                fullWidth
                                size="small"
                                error={!!errors.email}
                                helperText={errors.email}
                            />
                        </Grid>

                        <Grid item xs={12} sm={6}>
                            <FormControl fullWidth size="small" error={!!errors.marital_status}>
                                <InputLabel>Estado civil</InputLabel>
                                <Select
                                    label="Estado civil"
                                    name="marital_status"
                                    value={ownerForm.marital_status}
                                    onChange={handleDialogChange}
                                >
                                    {MARITAL_STATUS_CHOICES.map((opt) => (
                                        <MenuItem key={opt.value} value={opt.value}>
                                            {opt.label}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>

                        <Grid item xs={12} sm={6}>
                            <TextField
                                label="Nacionalidade"
                                name="nationality"
                                value={ownerForm.nationality}
                                onChange={handleDialogChange}
                                fullWidth
                                size="small"
                            />
                        </Grid>

                        <Grid item xs={12} sm={6}>
                            <DatePicker
                                label="Data de Nascimento"
                                value={ownerForm.birth_date ? new Date(ownerForm.birth_date + "T00:00:00") : null}
                                onChange={(newValue) =>
                                    setOwnerForm((prev) => ({
                                        ...prev,
                                        birth_date: newValue ? moment(newValue).format("YYYY-MM-DD") : null,
                                    }))
                                }
                                format="dd/MM/yyyy"
                                slotProps={{
                                    textField: {
                                        fullWidth: true,
                                        size: "small",
                                    },
                                }}
                            />
                        </Grid>

                        <Grid item xs={12}>
                            <TextField
                                label="Endereço"
                                name="address"
                                value={ownerForm.address}
                                onChange={handleDialogChange}
                                fullWidth
                                size="small"
                                multiline
                                minRows={2}
                                placeholder="Digite o endereço completo (logradouro, número, complemento, bairro, cidade/UF, CEP...)"
                            />
                        </Grid>
                    </Grid>
                </DialogContent>

                <Divider />
                <DialogActions sx={{ py: 3, display: "flex", justifyContent: "space-between" }}>
                    <Button onClick={() => setFormOpen(false)}>Cancelar</Button>
                    <Button onClick={handleSaveOwner} variant="contained">
                        Salvar
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    )
}
